import { Box, Button, IconButton, Typography } from "@mui/material";
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon,
} from "@mui/icons-material";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { PROJECT_ACCENT_VAR } from "@/components/project/ProjectThemeProvider";

interface GallerySelectionBarProps {
  visible: boolean;
  selectedCount: number;
  totalCount: number;
  downloading: boolean;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  onDownload: () => void;
  onClose: () => void;
}

export default function GallerySelectionBar({
  visible,
  selectedCount,
  totalCount,
  downloading,
  onSelectAll,
  onDeselectAll,
  onDownload,
  onClose,
}: GallerySelectionBarProps) {
  const { t } = useTranslation("gallery");
  const allSelected = selectedCount > 0 && selectedCount === totalCount;

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          style={{
            position: "fixed",
            bottom: "calc(24px + env(safe-area-inset-bottom, 0px))",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1300,
            maxWidth: "calc(100vw - 32px)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              bgcolor: "rgba(30, 30, 30, 0.95)",
              backdropFilter: "blur(10px)",
              borderRadius: 3,
              px: 3,
              py: 1.5,
              boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              border: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.9)", whiteSpace: "nowrap" }}
            >
              {t("selection.videosSelected", { count: selectedCount })}
            </Typography>

            <Button
              size="small"
              startIcon={allSelected ? <DeselectIcon /> : <SelectAllIcon />}
              onClick={allSelected ? onDeselectAll : onSelectAll}
              sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none" }}
            >
              {allSelected ? t("selection.deselectAll") : t("selection.selectAll")}
            </Button>

            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={onDownload}
              disabled={selectedCount === 0 || downloading}
              sx={{
                textTransform: "none",
                bgcolor: `var(${PROJECT_ACCENT_VAR})`,
                "&:hover": {
                  bgcolor: `var(${PROJECT_ACCENT_VAR})`,
                  filter: "brightness(0.85)",
                },
              }}
            >
              {downloading ? t("selection.downloading") : t("selection.downloadSelection")}
            </Button>

            <IconButton
              onClick={onClose}
              size="small"
              sx={{ color: "rgba(255,255,255,0.6)" }}
              aria-label={t("selection.exitSelection")}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
